import React from 'react';
import './EvidenceSourcesPanel.css';

type SourceCard = {
  id: string;
  label_en?: string;
  label_es?: string;
  blurb_en?: string;
  blurb_es?: string;
  status?: string;
  kind?: string;
};

type ConnectButton = {
  source_id?: string;
  provider?: string;
  label?: string;
  configured?: boolean;
};

type Props = {
  gatewayBaseUrl: string;
  accessToken: string;
  language?: 'en' | 'es';
  companyId?: number | null;
};

const statusLabel = (status: string, es: boolean): string => {
  if (status === 'connected') return es ? 'Listo' : 'Ready';
  if (status === 'needs_connect') return es ? 'Falta conectar' : 'Needs setup';
  if (status === 'error') return es ? 'Error' : 'Error';
  return status || (es ? 'Apagado' : 'Off');
};

const nextStepFor = (row: SourceCard, es: boolean): string => {
  const status = String(row.status || 'off');
  const kind = String(row.kind || '');
  if (status === 'connected') {
    return es
      ? 'Listo. No hay nada más que hacer aquí.'
      : 'Connected. Nothing else to do here.';
  }
  if (kind === 'oauth_mail' || kind === 'oauth_drive') {
    return es
      ? 'Pulse Conectar para autorizar con el IdP (Google). Solo lectura; puede revocar después.'
      : 'Click Connect to authorize with the IdP (Google). Read-only; you can revoke later.';
  }
  if (kind === 'local_cloud') {
    return es
      ? 'Conecte la nube (OAuth) o instale el agente para carpetas locales en este Mac.'
      : 'Connect the cloud (OAuth) or install the agent for local folders on this Mac.';
  }
  if (kind === 'local_folder') {
    return es
      ? 'Instale el agente en este Mac. Sin agente, NextCompli no puede ver carpetas locales.'
      : 'Install the agent on this Mac. Without it, NextCompli cannot see local folders.';
  }
  return es
    ? 'Instale el agente local para marcar este origen como listo.'
    : 'Install the local agent so this source can show as ready.';
};

/**
 * Sources — connect-only catalog (Email, Drive, Dropbox, OneDrive, This Mac).
 * Search / smart scan / drafts live on the task strip, not here.
 */
export default function EvidenceSourcesPanel({
  gatewayBaseUrl,
  accessToken,
  language = 'en',
  companyId = null,
}: Props): React.ReactElement {
  const es = language === 'es';
  const gateway = gatewayBaseUrl.replace(/\/$/, '');
  const [sources, setSources] = React.useState<SourceCard[]>([]);
  const [connectButtons, setConnectButtons] = React.useState<ConnectButton[]>([]);
  const [privacy, setPrivacy] = React.useState('');
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(true);
  const [connectBusy, setConnectBusy] = React.useState('');

  const load = React.useCallback(async () => {
    if (!accessToken || !gateway) return;
    setBusy(true);
    try {
      const query = companyId ? `?company_id=${encodeURIComponent(String(companyId))}` : '';
      const response = await fetch(`${gateway}/api/v1/evidence/sources${query}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const body = await response.json().catch(() => ({})) as {
        sources?: SourceCard[];
        connect_buttons?: ConnectButton[];
        privacy_note?: string;
        detail?: string;
      };
      if (!response.ok) {
        setError(typeof body.detail === 'string' ? body.detail : (es ? 'No se pudo cargar Fuentes.' : 'Could not load Sources.'));
        return;
      }
      setSources(Array.isArray(body.sources) ? body.sources : []);
      setConnectButtons(Array.isArray(body.connect_buttons) ? body.connect_buttons : []);
      setPrivacy(String(body.privacy_note || ''));
      setError('');
    } catch {
      setError(es ? 'No se pudo cargar Fuentes.' : 'Could not load Sources.');
    } finally {
      setBusy(false);
    }
  }, [accessToken, companyId, es, gateway]);

  const startConnect = React.useCallback(async (sourceId: string) => {
    if (!accessToken || !gateway || !sourceId) return;
    setConnectBusy(sourceId);
    setError('');
    try {
      const params = new URLSearchParams();
      if (companyId) params.set('company_id', String(companyId));
      params.set('return_url', `${window.location.origin}${window.location.pathname}`);
      const response = await fetch(
        `${gateway}/api/v1/evidence/sources/${encodeURIComponent(sourceId)}/connect?${params}`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      const body = await response.json().catch(() => ({})) as { auth_url?: string; detail?: string };
      if (!response.ok || !body.auth_url) {
        setError(typeof body.detail === 'string'
          ? body.detail
          : (es
            ? 'No se pudo iniciar la conexión. Configure el IdP en el gateway.'
            : 'Could not start connect. Configure the IdP on the gateway.'));
        return;
      }
      window.location.assign(body.auth_url);
    } catch {
      setError(es ? 'No se pudo iniciar la conexión.' : 'Could not start connect.');
    } finally {
      setConnectBusy('');
    }
  }, [accessToken, companyId, es, gateway]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const readyCount = sources.filter((row) => row.status === 'connected').length;
  const needCount = sources.length - readyCount;

  return (
    <section className="evidence-sources" aria-labelledby="evidence-sources-title" aria-busy={busy}>
      <header className="evidence-sources__head">
        <h4 id="evidence-sources-title" className="visually-hidden">
          {es ? 'Fuentes' : 'Sources'}
        </h4>
        {privacy ? <p className="evidence-sources__privacy">{privacy}</p> : null}
      </header>

      {busy ? (
        <p className="evidence-sources__note is-busy" role="status">
          <span className="evidence-sources__spinner" aria-hidden="true" />
          {es ? 'Cargando orígenes…' : 'Loading sources…'}
        </p>
      ) : (
        <p className="evidence-sources__summary" role="status">
          {es
            ? `${readyCount} listo(s)${needCount ? ` · ${needCount} por configurar` : ''}. Este Mac necesita el agente en el portátil.`
            : `${readyCount} ready${needCount ? ` · ${needCount} still need setup` : ''}. This Mac needs the laptop agent.`}
        </p>
      )}

      {error ? <p className="evidence-sources__error" role="alert">{error}</p> : null}

      <ul className="evidence-sources__grid" role="list">
        {sources.map((row) => {
          const id = String(row.id || '');
          const label = es ? (row.label_es || row.label_en || id) : (row.label_en || row.label_es || id);
          const blurb = es ? (row.blurb_es || row.blurb_en || '') : (row.blurb_en || row.blurb_es || '');
          const status = String(row.status || 'off');
          const ready = status === 'connected';
          return (
            <li key={id}>
              <article
                className={`evidence-sources__card${ready ? ' is-connected' : ' is-needs'}`}
                aria-label={`${label}: ${statusLabel(status, es)}`}
              >
                <span className="evidence-sources__card-top">
                  <strong>{label}</strong>
                  <em className={`evidence-sources__chip is-${status}`}>{statusLabel(status, es)}</em>
                </span>
                <span className="evidence-sources__blurb">{blurb}</span>
                {!ready ? (
                  <span className="evidence-sources__next">
                    <span className="evidence-sources__next-label">{es ? 'Qué hacer' : 'What to do'}</span>
                    {nextStepFor(row, es)}
                  </span>
                ) : null}
                <span className="evidence-sources__actions">
                  {!ready && connectButtons.some((btn) => btn.source_id === id) ? (
                    <button
                      type="button"
                      className="dashboard-btn dashboard-btn-primary evidence-sources__action"
                      disabled={Boolean(connectBusy) || busy}
                      onClick={() => void startConnect(id)}
                    >
                      {connectBusy === id
                        ? (es ? 'Abriendo…' : 'Opening…')
                        : (connectButtons.find((btn) => btn.source_id === id)?.label
                          || (es ? 'Conectar' : 'Connect'))}
                    </button>
                  ) : null}
                  {!ready && (String(row.kind || '').startsWith('local') || row.kind === 'local_folder' || row.kind === 'local_cloud') ? (
                    <a className="dashboard-btn evidence-sources__action" href="/console/agent">
                      {es ? 'Instalar / dispositivos' : 'Install / devices'}
                    </a>
                  ) : null}
                </span>
              </article>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
